import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert, Badge, Button, Card, CardBody, CardHeader, Col, Container,
    Form, FormGroup, Input, Label, Row, Spinner, Table
} from 'reactstrap';
import { FaArrowLeft, FaCheck, FaFileInvoice, FaSave, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { invoiceSupplierActions } from '../../sagas/invoiceSupplierSlice';
import './SupplierInvoiceForm.css';

const STEPS = ['Fournisseur', 'Bons de réception', 'Quantités', 'Vérification', 'Facture'];
const number = value => Number(value) || 0;
const money = value => `${number(value).toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DH`;
const round = value => Math.round((number(value) + Number.EPSILON) * 100) / 100;

const normalizeTax = value => {
    const tax = number(value);
    return tax > 1 ? tax / 100 : tax;
};

const calculateLine = line => {
    const quantite = Math.max(0, number(line.quantite));
    const prixUnitaireHT = Math.max(0, number(line.prixUnitaireHT));
    const tauxTVA = Math.max(0, number(line.tauxTVA));
    const montantHT = round(quantite * prixUnitaireHT);
    const montantTVA = round(montantHT * normalizeTax(tauxTVA));
    return {
        ...line,
        quantite,
        prixUnitaireHT,
        tauxTVA,
        prixUnitaireTTC: round(prixUnitaireHT * (1 + normalizeTax(tauxTVA))),
        montantHT,
        montantTVA,
        montantTTC: round(montantHT + montantTVA)
    };
};

const SupplierInvoiceForm = ({ initialValues, onSave, onCancel }) => {
    const dispatch = useDispatch();
    const loading = useSelector(state => state.invoiceSupplier?.loading);
    const activeCompany = useSelector(state => state.auth?.company);
    const [activeStep, setActiveStep] = useState(1);
    const [submitted, setSubmitted] = useState(false);
    const [values, setValues] = useState(() => ({
        ...initialValues,
        numeroFactfournisseur: initialValues?.numeroFactfournisseur || '',
        dateFacture: initialValues?.dateFacture || '',
        dateOperation: initialValues?.dateOperation || initialValues?.dateFacture || '',
        lignes: (initialValues?.lignes || []).map(calculateLine)
    }));

    const totals = useMemo(() => values.lignes.reduce((sum, line) => ({
        ht: round(sum.ht + number(line.montantHT)),
        tva: round(sum.tva + number(line.montantTVA)),
        ttc: round(sum.ttc + number(line.montantTTC))
    }), { ht: 0, tva: 0, ttc: 0 }), [values.lignes]);

    const receipts = useMemo(() => {
        const mapped = new Map((values.receiptMappings || []).map(receipt => [Number(receipt.id), { ...receipt, lines: [] }]));
        values.lignes.forEach(line => {
            const id = Number(line.sourceBonReceptionId);
            if (!mapped.has(id)) mapped.set(id, { id, numero: line.sourceBonReceptionNumero, lines: [] });
            mapped.get(id).lines.push(line);
        });
        return [...mapped.values()].filter(receipt => receipt.id);
    }, [values.lignes, values.receiptMappings]);

    const covered = Math.min(number(values.montantRegle), totals.ttc);
    const remaining = Math.max(0, round(totals.ttc - covered));
    const returnedAmount = round(values.lignes.reduce((sum, line) =>
        sum + number(line.quantiteRetournee) * number(line.prixUnitaireTTC), 0));
    const companyLabel = values.companyName
        || (Number(activeCompany?.id) === Number(values.companyId) ? activeCompany?.name : null)
        || '-';

    const updateField = event => {
        const { name, value } = event.target;
        setValues(current => ({ ...current, [name]: value }));
    };

    const updateLine = (index, field, value) => {
        setValues(current => ({
            ...current,
            lignes: current.lignes.map((line, lineIndex) => lineIndex === index
                ? calculateLine({ ...line, [field]: value })
                : line)
        }));
    };

    const removeLine = index => setValues(current => ({
        ...current,
        lignes: current.lignes.filter((_, lineIndex) => lineIndex !== index)
    }));

    const validateStep = step => {
        if (step === 1 && (!values.fournisseurId || !values.companyId || !values.numeroFactfournisseur.trim() || !values.dateFacture)) {
            Swal.fire({ icon: 'warning', title: 'Informations incomplètes', text: 'La référence fournisseur et la date de facture sont obligatoires.' });
            return false;
        }
        if (step === 2 && receipts.length === 0) {
            Swal.fire({ icon: 'warning', title: 'Aucun BR', text: 'Cette facture doit conserver au moins un bon de réception source.' });
            return false;
        }
        if (step === 3 && (values.lignes.length === 0 || values.lignes.some(line => number(line.quantite) <= 0 || number(line.quantite) > number(line.quantiteRestanteFacturable)))) {
            Swal.fire({ icon: 'warning', title: 'Quantités invalides', text: 'Chaque quantité doit être positive et ne pas dépasser le reste facturable.' });
            return false;
        }
        return true;
    };

    const next = () => {
        if (validateStep(activeStep)) setActiveStep(step => Math.min(5, step + 1));
    };

    const submit = statut => {
        setSubmitted(true);
        if (![1, 2, 3].every(validateStep)) return;
        const payload = {
            ...values,
            companyId: Number(values.companyId),
            fournisseurId: Number(values.fournisseurId),
            referenceBons: receipts.map(receipt => receipt.numero).filter(Boolean).join(','),
            statut,
            montantHT: totals.ht,
            montantTVA: totals.tva,
            montantTTC: totals.ttc,
            montantRegle: number(values.montantRegle),
            lignes: values.lignes.map(calculateLine)
        };
        dispatch(invoiceSupplierActions.createInvoice({
            invoice: payload,
            onSuccess: data => {
                Swal.fire({ icon: 'success', title: statut === 'BROUILLON' ? 'Brouillon enregistré' : 'Facture fournisseur créée' });
                onSave?.(data);
            },
            onError: error => Swal.fire({
                icon: 'error',
                title: 'Création impossible',
                text: error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Une erreur est survenue.'
            })
        }));
    };

    return (
        <Container fluid className="supplier-invoice-form py-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                <div>
                    <h3 className="mb-1"><FaFileInvoice className="me-2 text-primary" />Nouvelle facture fournisseur</h3>
                    <div className="text-muted">Facturation des bons de réception sélectionnés</div>
                </div>
                <Button type="button" color="light" className="border" onClick={onCancel}><FaArrowLeft className="me-2" />Retour</Button>
            </div>

            <div className="invoice-steps mb-3">
                {STEPS.map((label, index) => {
                    const step = index + 1;
                    return <button type="button" key={label} className={`invoice-step ${activeStep === step ? 'active' : ''} ${activeStep > step ? 'done' : ''}`} onClick={() => setActiveStep(step)}>
                        <span>{activeStep > step ? <FaCheck /> : step}</span><strong>{label}</strong>
                    </button>;
                })}
            </div>

            <Form onSubmit={event => event.preventDefault()}>
                <Card className="invoice-main-card shadow-sm">
                    <CardHeader className="bg-white"><h5 className="mb-0">{activeStep}. {STEPS[activeStep - 1]}</h5></CardHeader>
                    <CardBody>
                        {activeStep === 1 && <Row className="g-3">
                            <Col md="6"><FormGroup><Label>Fournisseur</Label><Input value={values.fournisseurNom || ''} disabled /></FormGroup></Col>
                            <Col md="6"><FormGroup><Label>Référence facture fournisseur *</Label><Input name="numeroFactfournisseur" value={values.numeroFactfournisseur} onChange={updateField} invalid={submitted && !values.numeroFactfournisseur.trim()} autoFocus /></FormGroup></Col>
                            <Col md="4"><FormGroup><Label>Date de facture *</Label><Input type="date" name="dateFacture" value={values.dateFacture} onChange={updateField} /></FormGroup></Col>
                            <Col md="4"><FormGroup><Label>Date d'opération</Label><Input type="date" name="dateOperation" value={values.dateOperation} onChange={updateField} /></FormGroup></Col>
                            <Col md="4"><FormGroup><Label>Entreprise</Label><Input value={companyLabel} disabled /></FormGroup></Col>
                            <Col xs="12"><Alert color="info" className="mb-0">Le fournisseur et l'entreprise proviennent des BR sélectionnés et restent verrouillés pour garantir la cohérence documentaire.</Alert></Col>
                        </Row>}

                        {activeStep === 2 && <div>
                            <div className="d-flex justify-content-between mb-3"><div><h5 className="mb-1">Bons de réception sélectionnés</h5><small className="text-muted">Un BR apparaît une seule fois avec ses montants et sa couverture.</small></div><Badge color="primary" pill>{receipts.length} BR</Badge></div>
                            <div className="table-responsive"><Table hover className="align-middle invoice-table"><thead><tr><th>Numéro / date</th><th className="text-end">Montant net</th><th className="text-end">Règlements</th><th className="text-end">Reste</th><th className="text-end">Lignes</th></tr></thead><tbody>
                                {receipts.map(receipt => {
                                    const invoiceAmount = receipt.lines.reduce((sum, line) => sum + number(line.montantTTC), 0);
                                    const paid = Math.min(number(receipt.montantRegle), invoiceAmount);
                                    return <tr key={receipt.id}><td><strong>{receipt.numero || `BR #${receipt.id}`}</strong><div className="text-muted">{receipt.dateReception || '-'}</div></td><td className="text-end fw-bold">{money(invoiceAmount)}</td><td className="text-end text-success">{money(paid)}</td><td className="text-end">{money(invoiceAmount - paid)}</td><td className="text-end">{receipt.lines.length}</td></tr>;
                                })}
                            </tbody></Table></div>
                        </div>}

                        {activeStep === 3 && <div className="table-responsive"><Table hover className="align-middle invoice-table"><thead><tr><th>BR</th><th>Article</th><th className="text-end">Reçue</th><th className="text-end">Retours</th><th className="text-end">Déjà facturée</th><th style={{ minWidth: 125 }}>À facturer</th><th style={{ minWidth: 130 }}>PU HT</th><th style={{ minWidth: 105 }}>TVA %</th><th className="text-end">Total TTC</th><th /></tr></thead><tbody>
                            {values.lignes.map((line, index) => <tr key={`${line.sourceLigneReceptionId || index}-${index}`}>
                                <td><strong>{line.sourceBonReceptionNumero || '-'}</strong></td><td><strong>{line.articleNom || `Article #${line.articleId}`}</strong><div className="text-muted">{line.unite || ''}</div></td>
                                <td className="text-end">{number(line.quantiteRecue)}</td><td className="text-end text-danger">{number(line.quantiteRetournee)}</td><td className="text-end">{number(line.quantiteDejaFacturee)}</td>
                                <td><Input type="number" min="0.001" step="0.001" max={number(line.quantiteRestanteFacturable)} value={line.quantite} onChange={event => updateLine(index, 'quantite', event.target.value)} /><small className="text-muted">max. {number(line.quantiteRestanteFacturable)}</small></td>
                                <td><Input type="number" min="0" step="0.01" value={line.prixUnitaireHT} onChange={event => updateLine(index, 'prixUnitaireHT', event.target.value)} /></td>
                                <td><Input type="number" min="0" step="0.01" value={normalizeTax(line.tauxTVA) * 100} onChange={event => updateLine(index, 'tauxTVA', number(event.target.value) / 100)} /></td>
                                <td className="text-end fw-bold">{money(line.montantTTC)}</td><td><Button type="button" color="link" className="text-danger" title="Retirer la ligne" onClick={() => removeLine(index)}><FaTrash /></Button></td>
                            </tr>)}
                        </tbody></Table></div>}

                        {activeStep === 4 && <Row className="g-3">
                            <Col md="4"><div className="review-tile"><span>Paiements associés aux BR</span><strong className="text-success">{money(covered)}</strong><small>Transférés à la confirmation, sans nouveau décaissement.</small></div></Col>
                            <Col md="4"><div className="review-tile"><span>Retours fournisseur</span><strong>Déduits par ligne</strong><small>Les quantités retournées ne peuvent pas être facturées.</small></div></Col>
                            <Col md="4"><div className="review-tile"><span>Reste prévisionnel</span><strong className={remaining ? 'text-danger' : 'text-success'}>{money(remaining)}</strong><small>Le serveur recalculera le solde final.</small></div></Col>
                            <Col xs="12"><Alert color="info" className="mb-0">Les quantités, retours, factures existantes et règlements des BR seront revérifiés dans une seule transaction.</Alert></Col>
                        </Row>}

                        {activeStep === 5 && <Card className="border-success"><CardHeader className="bg-success bg-opacity-10"><strong>Vérification avant enregistrement</strong></CardHeader><CardBody><Row className="g-3">
                            <Col md="6"><small className="text-muted d-block">Fournisseur</small><strong>{values.fournisseurNom}</strong></Col><Col md="3"><small className="text-muted d-block">Référence</small><strong>{values.numeroFactfournisseur || '-'}</strong></Col><Col md="3"><small className="text-muted d-block">Date</small><strong>{values.dateFacture || '-'}</strong></Col>
                            <Col md="6"><small className="text-muted d-block">Documents sources</small><strong>{receipts.map(receipt => receipt.numero).join(', ')}</strong></Col><Col md="3"><small className="text-muted d-block">Lignes</small><strong>{values.lignes.length}</strong></Col><Col md="3"><small className="text-muted d-block">Statut après confirmation</small><Badge color={remaining ? 'warning' : 'success'}>{remaining ? 'IMPAYÉE' : 'PAYÉE'}</Badge></Col>
                        </Row></CardBody></Card>}

                        <div className="invoice-summary mt-4"><div><small>Total HT</small><strong>{money(totals.ht)}</strong></div><div><small>TVA</small><strong>{money(totals.tva)}</strong></div><div><small>Total TTC net</small><strong>{money(totals.ttc)}</strong></div><div className="is-return"><small>Retours déduits</small><strong>− {money(returnedAmount)}</strong></div><div><small>Couvert par BR</small><strong>{money(covered)}</strong></div><div><small>Reste attendu</small><strong>{money(remaining)}</strong></div></div>

                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4">
                            <Button type="button" color="light" className="border" onClick={onCancel}>Annuler</Button>
                            <div className="d-flex gap-2">
                                {activeStep > 1 && <Button type="button" color="light" onClick={() => setActiveStep(step => step - 1)}>Précédent</Button>}
                                {activeStep < 5 && <Button type="button" color="primary" onClick={next}>Continuer</Button>}
                                {activeStep === 5 && <><Button type="button" color="secondary" disabled={loading} onClick={() => submit('BROUILLON')}><FaSave className="me-2" />Brouillon</Button><Button type="button" color="success" disabled={loading} onClick={() => submit('IMPAYE')}>{loading ? <Spinner size="sm" /> : <FaCheck className="me-2" />}Créer et confirmer</Button></>}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Form>
        </Container>
    );
};

export default SupplierInvoiceForm;
